import { matchPath } from 'react-router-dom'

import { requiresProductProblem } from '@/sdk/contract'

import type {
  CallsPlugin,
  DataBenchPlugin,
  PluginCanvasManifest,
  TenantPluginRegister,
  YeshumanPluginApi,
  YeshumanPluginSlots,
} from './types'

const emptyCalls = (): CallsPlugin => ({
  rowSubtitle: null,
  recordBadges: [],
  recordPanels: [],
  boardActions: null,
})

const emptyDataBench = (): DataBenchPlugin => ({ scoreActions: [] })

export const defaultPluginSlots: Readonly<YeshumanPluginSlots> = Object.freeze({
  landingOverride: null,
  hideCardDescription: false,
  emphasizeJobFacts: false,
  publicShareBodyMaxWidthPx: null,
  candidateHome: 'default',
  employerRecentApplicants: false,
  employerMatchingProfiles: false,
  candidateSectionsSkeleton: false,
  employerSectionsSkeleton: false,
  guestBackgroundClassName: null,
  background: null,
  hasBackgroundDecor: false,
  guestMarketingShellBackground: true,
  callsSimUi: false,
  calls: emptyCalls(),
  dataBench: emptyDataBench(),
  canvases: [],
  tenantLogoFallback: false,
  conversationalBrief: null,
  accessRequestAdmin: null,
  pluginRoutes: [],
  publicShareRoutes: [],
  dashboardSections: {},
  focusRegistrations: [],
  navItems: {},
  namedRoutes: {},
})

function freshSlots(): YeshumanPluginSlots {
  return {
    ...defaultPluginSlots,
    pluginRoutes: [],
    publicShareRoutes: [],
    dashboardSections: {},
    focusRegistrations: [],
    navItems: {},
    namedRoutes: {},
    calls: emptyCalls(),
    dataBench: emptyDataBench(),
    canvases: [],
  }
}

const slots: YeshumanPluginSlots = freshSlots()

export function getPluginSlots(): YeshumanPluginSlots {
  return slots
}

/** Build `/orders/42` from a pattern registered with `routes.named`. */
export function resolveRoute(
  name: string,
  params: Record<string, string | number> = {},
  registry: Record<string, string> = slots.namedRoutes,
): string | null {
  const pattern = registry[name]
  if (!pattern) return null
  let missing = false
  const path = pattern.replace(/:([A-Za-z0-9_]+)/g, (_, key: string) => {
    const value = params[key]
    if (value === undefined || value === '') {
      missing = true
      return ''
    }
    return encodeURIComponent(String(value))
  })
  return missing ? null : path
}

export function createPluginApi(target: YeshumanPluginSlots = freshSlots()): YeshumanPluginApi {
  const register = (partial: Partial<YeshumanPluginSlots>) => {
    Object.assign(target, partial)
  }
  return {
    slots: target,
    register,
    landing: {
      override(name) {
        target.landingOverride = name
      },
    },
    dashboard: {
      section(name, variant) {
        target.dashboardSections[name] = variant
        if (name === 'candidate') {
          target.candidateHome = variant === 'sections' ? 'sections' : 'default'
          target.candidateSectionsSkeleton = variant === 'sections'
        } else if (name === 'employer') {
          target.employerRecentApplicants = true
          target.employerMatchingProfiles = true
          target.employerSectionsSkeleton = true
        }
      },
    },
    chrome: {
      background(component) {
        target.background = component
        target.hasBackgroundDecor = true
      },
      card(opts) {
        if (opts.hideDescription) target.hideCardDescription = true
      },
    },
    routes: {
      add(route) {
        target.pluginRoutes.push(route)
      },
      named(name, pattern) {
        target.namedRoutes[name] = pattern
      },
    },
    nav: {
      add(focus, items) {
        target.navItems[focus] = [...(target.navItems[focus] ?? []), ...items]
      },
    },
    focus: {
      register(registration) {
        const index = target.focusRegistrations.findIndex((r) => r.focus === registration.focus)
        if (index === -1) {
          target.focusRegistrations.push(registration)
          return
        }
        const existing = target.focusRegistrations[index]
        target.focusRegistrations[index] = {
          ...existing,
          ...registration,
          routes: [...(existing.routes ?? []), ...(registration.routes ?? [])],
        }
      },
    },
    publicShare: {
      layout(opts) {
        if (typeof opts.maxWidthPx === 'number') {
          target.publicShareBodyMaxWidthPx = opts.maxWidthPx
        }
      },
      route(route) {
        target.publicShareRoutes.push(route)
      },
    },
    calls: {
      rowSubtitle(fn) {
        target.calls.rowSubtitle = fn
      },
      recordBadge(component) {
        target.calls.recordBadges.push(component)
      },
      recordPanel(component) {
        target.calls.recordPanels.push(component)
      },
      boardActions(component) {
        target.calls.boardActions = component
      },
    },
    dataBench: {
      scoreAction(component) {
        target.dataBench.scoreActions.push(component)
      },
    },
    accessRequests: {
      admin(plugin) {
        target.accessRequestAdmin = plugin
      },
    },
    canvases: {
      register(manifest) {
        const problem = manifest.requiresProduct ? requiresProductProblem(manifest.requiresProduct) : null
        if (problem) {
          console.warn(`[plugin] canvas ${manifest.id} skipped: ${problem}`)
          return false
        }
        target.canvases = [...target.canvases.filter((c) => c.id !== manifest.id), manifest]
        return true
      },
    },
  }
}

/** Canvases with a page target matching `pathname`, with that target's route params. */
export function canvasesForPage(
  pathname: string,
  canvases: PluginCanvasManifest[] = slots.canvases,
): { manifest: PluginCanvasManifest; params: Record<string, string> }[] {
  const matches: { manifest: PluginCanvasManifest; params: Record<string, string> }[] = []
  for (const manifest of canvases) {
    for (const target of manifest.targets) {
      if (!('page' in target)) continue
      const match = matchPath({ path: target.page, end: true }, pathname)
      if (match) {
        matches.push({ manifest, params: { ...match.params } as Record<string, string> })
        break
      }
    }
  }
  return matches
}

export function canvasesForEntity(
  kind: string,
  canvases: PluginCanvasManifest[] = slots.canvases,
): PluginCanvasManifest[] {
  return canvases.filter((manifest) =>
    manifest.targets.some((target) => 'entity' in target && target.entity === kind),
  )
}

export function findCanvas(
  id: string,
  canvases: PluginCanvasManifest[] = slots.canvases,
): PluginCanvasManifest | null {
  return canvases.find((manifest) => manifest.id === id) ?? null
}

/** The manifest's `page.path` with params (and `:id` from the entity) filled, or null. */
export function canvasPagePath(
  manifest: PluginCanvasManifest,
  params: Record<string, string>,
  entity: { kind: string; id: string } | null = null,
): string | null {
  if (!manifest.page) return null
  const values: Record<string, string> = { ...(entity ? { id: entity.id } : {}), ...params }
  return resolveRoute(manifest.id, values, { [manifest.id]: manifest.page.path })
}

export function applyTenantPlugin(register: TenantPluginRegister | undefined) {
  if (!register) return
  register(createPluginApi(slots))
}
